var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CancelIcon from "@mui/icons-material/Cancel";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef, useState } from "react";
var ACTIVE_BORDER_COLOR = "rgba(33, 201, 227, 0.6)";
export function ElementChip(_a) {
    var text = _a.text, _b = _a.variant, variant = _b === void 0 ? "outlined" : _b, startIcon = _a.startIcon, borderColor = _a.borderColor, backgroundColor = _a.backgroundColor, tooltipContent = _a.tooltipContent, _c = _a.active, active = _c === void 0 ? false : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, onClick = _a.onClick, onDelete = _a.onDelete;
    var _e = useState(false), isExpanded = _e[0], setIsExpanded = _e[1];
    var _f = useState(false), isTruncated = _f[0], setIsTruncated = _f[1];
    var textRef = useRef(null);
    useEffect(function () {
        var element = textRef.current;
        if (!element)
            return;
        var checkTruncation = function () {
            if (!isExpanded) {
                setIsTruncated(element.scrollWidth > element.clientWidth);
            }
        };
        checkTruncation();
        var resizeObserver = typeof ResizeObserver === "undefined"
            ? undefined
            : new ResizeObserver(checkTruncation);
        resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.observe(element);
        window.addEventListener("resize", checkTruncation);
        return function () {
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            window.removeEventListener("resize", checkTruncation);
        };
    }, [text, isExpanded]);
    var showFull = isExpanded;
    var expandable = isTruncated || isExpanded;
    var clickable = !disabled && (Boolean(onClick) || expandable);
    var isGhost = variant === "ghost" && !active;
    return (_jsx(Tooltip, __assign({ title: tooltipContent !== null && tooltipContent !== void 0 ? tooltipContent : text, enterTouchDelay: tooltipContent ? 0 : undefined, arrow: true }, { children: _jsxs(Box, __assign({ component: "span", "data-element-chip": true, "data-active": active ? "true" : undefined, "data-disabled": disabled ? "true" : undefined, sx: {
                "@keyframes elementChipGlowShiftCyan": {
                    "0%, 100%": {
                        boxShadow: "0 0 6px rgba(33, 201, 227, 0.4), 0 0 12px rgba(33, 201, 227, 0.3)",
                    },
                    "50%": {
                        boxShadow: "0 0 10px rgba(33, 201, 227, 0.5), 0 0 18px rgba(33, 201, 227, 0.4)",
                    },
                },
                display: "inline-flex",
                alignItems: showFull ? "flex-start" : "center",
                height: showFull ? "auto" : "28px",
                py: showFull ? "4px" : 0,
                maxWidth: "100%",
                minWidth: 0,
                borderRadius: "6px",
                border: function (theme) { return "1px solid ".concat(isGhost
                    ? "transparent"
                    : active
                        ? ACTIVE_BORDER_COLOR
                        : borderColor !== null && borderColor !== void 0 ? borderColor : theme.palette.divider); },
                backgroundColor: isGhost
                    ? "transparent"
                    : backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : "background.paper",
                color: "text.primary",
                opacity: disabled ? 0.55 : 1,
                boxShadow: active
                    ? "0 0 6px rgba(33, 201, 227, 0.4), 0 0 12px rgba(33, 201, 227, 0.3)"
                    : isGhost
                        ? "none"
                        : "0 1px 1px rgba(15, 23, 42, 0.03)",
                animation: active ? "elementChipGlowShiftCyan 2s ease-in-out infinite" : "none",
                transition: "background-color 0.2s ease-in-out, box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out",
                whiteSpace: showFull ? "normal" : "nowrap",
                "&:hover": isGhost && clickable ? {
                    backgroundColor: "action.hover",
                } : undefined,
                "@media print": {
                    pointerEvents: "none !important",
                    animation: "none !important",
                    boxShadow: "none !important",
                    backgroundColor: "#fff !important",
                    color: "#1f2937 !important",
                    border: "1px solid #ccc !important",
                    height: "22px !important",
                    py: "0px !important",
                    alignItems: "center !important",
                    whiteSpace: "nowrap !important",
                },
            } }, { children: [_jsxs(Box, __assign({ component: "span", "data-element-chip-action": true, tabIndex: !disabled && clickable ? 0 : undefined, role: onClick || clickable ? "button" : undefined, "aria-disabled": onClick && disabled ? true : undefined, "aria-expanded": !onClick && expandable ? isExpanded : undefined, onClick: function (event) {
                        if (disabled)
                            return;
                        if (onClick) {
                            event.stopPropagation();
                            onClick(event.currentTarget);
                            return;
                        }
                        var element = textRef.current;
                        var canToggle = isExpanded
                            || (!!element && element.scrollWidth > element.clientWidth);
                        if (!canToggle)
                            return;
                        event.stopPropagation();
                        setIsExpanded(function (current) { return !current; });
                    }, onKeyDown: function (event) {
                        if (disabled)
                            return;
                        if ((event.key !== "Enter" && event.key !== " ") || !clickable)
                            return;
                        event.preventDefault();
                        event.stopPropagation();
                        if (onClick) {
                            onClick(event.currentTarget);
                        }
                        else {
                            setIsExpanded(function (current) { return !current; });
                        }
                    }, sx: {
                        display: "inline-flex",
                        alignItems: showFull ? "flex-start" : "center",
                        gap: startIcon ? "5px" : 0,
                        flex: "1 1 auto",
                        minWidth: 0,
                        // Fill the compact chip so its entire body is an actionable hit target.
                        alignSelf: "stretch",
                        pl: "8px",
                        pr: onDelete ? "3px" : "8px",
                        cursor: disabled ? "not-allowed" : clickable ? "pointer" : "inherit",
                        userSelect: clickable ? "none" : "auto",
                        "@media print": {
                            pl: "6px !important",
                            pr: "6px !important",
                        },
                    } }, { children: [startIcon && (_jsx(Box, __assign({ component: "span", "data-element-chip-icon": true, sx: {
                                display: "inline-flex",
                                alignItems: "center",
                                flex: "0 0 auto",
                                mt: showFull ? "2px" : 0,
                                "@media print": {
                                    mt: "0px !important",
                                    transform: "scale(0.85) !important",
                                },
                            } }, { children: startIcon }))), _jsx(Box, __assign({ ref: textRef, component: "span", "data-element-chip-label": true, sx: {
                                minWidth: 0,
                                overflow: showFull ? "visible" : "hidden",
                                textOverflow: showFull ? "clip" : "ellipsis",
                                whiteSpace: showFull ? "normal" : "nowrap",
                                wordBreak: showFull ? "break-word" : "normal",
                                lineHeight: "16px",
                                mt: "2px",
                                "@media print": {
                                    fontSize: "11px !important",
                                    lineHeight: "14px !important",
                                    mt: "0px !important",
                                    overflow: "hidden !important",
                                    textOverflow: "ellipsis !important",
                                    whiteSpace: "nowrap !important",
                                },
                            } }, { children: text }))] })), onDelete && (_jsx(IconButton, __assign({ size: "small", "data-element-chip-delete": true, "aria-label": "Delete ".concat(text), disabled: disabled, onPointerDown: function (event) { return event.stopPropagation(); }, onClick: function (event) {
                        event.stopPropagation();
                        onDelete();
                    }, sx: {
                        flex: "0 0 auto",
                        alignSelf: showFull ? "flex-start" : "center",
                        width: "20px",
                        height: "20px",
                        p: 0,
                        mr: "3px",
                        color: "text.secondary",
                        "&:hover": {
                            color: "error.main",
                            backgroundColor: "action.hover",
                        },
                        "@media print": {
                            display: "none !important",
                        },
                    } }, { children: _jsx(CancelIcon, { sx: { fontSize: "16px" } }) })))] })) })));
}
